// File: src/components/NavigationBar.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUser, logout } from "../services/auth";
import { useAccess } from "./PermissionRoute";
import "../styles/NavigationBar.css";

function NavigationBar({ showFilterButton = false, onToggleFilter }) {
  const navigate           = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productSummary, setProductSummary] = useState(null);
  const [expanded, setExpanded] = useState({
    shopping: false,
    meals:    false,
    admin:    false,
    scraping: false,
    dashboardAdmin: false,
    platformAdmin: false,
    appAdmin: false,
    emailAdmin: false,
    billing: false,
    userAdmin: false,
    supportAdmin: false,
    enterprise: false,
    news: false,
    account:  false
  });

  const { loading: accessLoading, canAccessPath } = useAccess();
  const currentUser = getCurrentUser();
  const showPath = (to) => !accessLoading && canAccessPath(to);
  const showSection = (...paths) => paths.some((path) => showPath(path));
  const NavSubLink = ({ to, children }) => (
    showPath(to) ? (
      <li className="nav-sub-item">
        <Link to={to} onClick={closeDrawer}>{children}</Link>
      </li>
    ) : null
  );

  const toggleDrawer = () => setDrawerOpen(o => !o);
  const closeDrawer  = () => setDrawerOpen(false);
  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };
  const toggleSection = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // Works even if parent didn't pass onToggleFilter (CardView listens for window "toggleFilter")
  const emitToggleFilter = () => {
    if (typeof onToggleFilter === "function") onToggleFilter();
    else window.dispatchEvent(new Event("toggleFilter"));
  };

  useEffect(() => {
    document.body.classList.toggle("nav-open", drawerOpen);
  }, [drawerOpen]);

  useEffect(() => {
    const handleProductSummary = (event) => {
      setProductSummary(event.detail || null);
    };

    window.addEventListener("product-details-sticky-summary", handleProductSummary);
    return () => {
      window.removeEventListener("product-details-sticky-summary", handleProductSummary);
    };
  }, []);

  return (
    <div>
      {/* Responsive top bar; desktop only appears for product sticky summaries. */}
      {!drawerOpen && (
        <div
          className={`top-bar${productSummary ? " top-bar--has-product-summary" : ""}${productSummary?.visible ? " top-bar--product-summary-visible" : ""}`}
        >
          <button
            type="button"
            className="hamburger"
            onClick={toggleDrawer}
            aria-label="Open navigation"
          >
            <span className="hamburger-icon" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
          {productSummary && (
            <div
              className={`top-bar-product-summary ${productSummary.visible ? "top-bar-product-summary--visible" : ""}`}
              aria-hidden={!productSummary.visible}
            >
              <img
                src={productSummary.imageSrc}
                data-fallback-src={productSummary.fallbackSrc || undefined}
                alt=""
                className="top-bar-product-summary__image"
              />
              <div className="top-bar-product-summary__copy">
                <div className="top-bar-product-summary__brand">{productSummary.brand}</div>
                <div className="top-bar-product-summary__name">{productSummary.name}</div>
              </div>
            </div>
          )}
          {showFilterButton && (
            <button className="mobile-top-filter-btn" onClick={emitToggleFilter}>
              • Filter
            </button>
          )}
        </div>
      )}

      {/* Sidebar drawer */}
      <div className={`sidebar ${drawerOpen ? "open" : ""}`}>
        <div className="mobile-close" onClick={toggleDrawer}>&times;</div>
        <h2>Zuno</h2>

        <nav>
          <ul>
            {showPath("/dashboard") && (
              <li>
                <Link to="/dashboard" onClick={closeDrawer}>Dashboard</Link>
              </li>
            )}

            {showSection("/shopping-list", "/recent-purchases", "/watch-list", "/barcode-lookup") && (
              <>
                <li className="nav-section-heading" onClick={() => toggleSection("shopping")}>
                  <span>Shopping</span>
                  <span className="nav-toggle-icon">{expanded.shopping ? "×" : "+"}</span>
                </li>
                {expanded.shopping && (
                  <>
                    <NavSubLink to="/shopping-list">Shopping List</NavSubLink>
                    <NavSubLink to="/recent-purchases">Recent Purchases</NavSubLink>
                    <NavSubLink to="/watch-list">Watch List</NavSubLink>
                    <NavSubLink to="/barcode-lookup">Barcode Lookup</NavSubLink>
                  </>
                )}
              </>
            )}

            {showSection("/meals", "/planner", "/manual-meal-prep/new", "/meal/chat-meal-planner", "/meal/chat-meal-planner-classic", "/meal/chat-meal-planner-elevated") && (
              <>
                <li className="nav-section-heading" onClick={() => toggleSection("meals")}>
                  <span>Meals</span>
                  <span className="nav-toggle-icon">{expanded.meals ? "×" : "+"}</span>
                </li>
                {expanded.meals && (
                  <>
                    <NavSubLink to="/meals">Created Meals</NavSubLink>
                    <NavSubLink to="/planner">Weekly Planner</NavSubLink>
                    <NavSubLink to="/manual-meal-prep/new">Create Meal</NavSubLink>
                    <NavSubLink to="/meal/chat-meal-planner">Chat Meal Planner - Builder</NavSubLink>
                    <NavSubLink to="/meal/chat-meal-planner-classic">Chat Meal Planner - Classic</NavSubLink>
                    <NavSubLink to="/meal/chat-meal-planner-elevated">Chat Meal Planner - Elevated</NavSubLink>
                  </>
                )}
              </>
            )}

            {showSection(
              "/meal-planner",
              "/product-comparison",
              "/admin/coles-scraper",
              "/admin/coles-scraper-progress",
              "/admin/woolies-scraper",
              "/admin/aldi-scraper",
              "/admin/iga-scraper",
              "/admin/amazon-gtin-review",
              "/admin/cron-admin",
              "/admin/ios-builder",
              "/admin/master-sitemap",
              "/admin/coles-latest",
              "/admin/scrape-date",
              "/admin/category-paths",
              "/admin/hotprices-importer",
              "/admin/data-holdings-importer",
              "/admin/latest-extract",
              "/admin/image-selector",
              "/admin/barcode-scanner",
              "/admin/global-identifier-key",
              "/admin/cardview-weekly",
              "/admin/priority-products",
              "/admin/platform-status",
              "/admin/file-count",
              "/admin/content-protection",
              "/admin/login-page-designer",
              "/admin/email-lists",
              "/admin/email-campaigns",
              "/admin/email-template",
              "/admin/email-config",
              "/admin/email-flows",
              "/admin/stripe-config",
              "/admin/subscriptions",
              "/admin/package-permissions",
              "/admin/stripe-events",
              "/admin/stripe-analytics",
              "/admin/support-tickets",
              "/admin/support-manager",
              "/admin/users",
              "/admin/user-audit-log",
              "/admin/product-analytics",
              "/admin/new-products",
              "/admin/discontinued-products",
              "/admin/price-changes",
              "/admin/custom-reports",
              "/admin/export-library",
              "/admin/zuno",
              "/admin/news-articles",
              "/admin/enterprise-news-articles",
              "/admin/add-news-article",
              "/admin/news-article-admin",
              "/admin/news-article-analytics"
            ) && (
              <>
                {showSection("/meal-planner", "/product-comparison") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection("admin")}>
                      <span>Admin</span>
                      <span className="nav-toggle-icon">{expanded.admin ? "×" : "+"}</span>
                    </li>
                    {expanded.admin && (
                      <>
                        <NavSubLink to="/meal-planner">Meal Planner</NavSubLink>
                        <NavSubLink to="/product-comparison">Product Comparison</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/coles-scraper", "/admin/coles-scraper-progress", "/admin/woolies-scraper", "/admin/aldi-scraper", "/admin/iga-scraper", "/admin/amazon-scraper", "/admin/amazon-gtin-review", "/admin/master-sitemap", "/admin/scrape-date", "/admin/category-paths", "/admin/hotprices-importer", "/admin/data-holdings-importer", "/admin/cron-admin") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('scraping')}>
                      <span>Scraping Tools</span>
                      <span className="nav-toggle-icon">{expanded.scraping ? '×' : '+'}</span>
                    </li>
                    {expanded.scraping && (
                      <>
                        <NavSubLink to="/admin/coles-scraper">Coles Scraper</NavSubLink>
                        <NavSubLink to="/admin/coles-scraper-progress">Coles Scraper Progress</NavSubLink>
                        <NavSubLink to="/admin/woolies-scraper">Woolies Scraper</NavSubLink>
                        <NavSubLink to="/admin/aldi-scraper">Aldi Scraper</NavSubLink>
                        <NavSubLink to="/admin/iga-scraper">IGA Scraper</NavSubLink>
                        <NavSubLink to="/admin/amazon-scraper">Amazon Scraper</NavSubLink>
                        <NavSubLink to="/admin/amazon-gtin-review">Amazon GTIN Review</NavSubLink>
                        <NavSubLink to="/admin/master-sitemap">Master Sitemap</NavSubLink>
                        <NavSubLink to="/admin/scrape-date">Scrape Date Admin</NavSubLink>
                        <NavSubLink to="/admin/category-paths">Category Paths</NavSubLink>
                        <NavSubLink to="/admin/hotprices-importer">HotPrices Importer</NavSubLink>
                        <NavSubLink to="/admin/data-holdings-importer">Data Holdings Importer</NavSubLink>
                        <NavSubLink to="/admin/cron-admin">Cron Admin</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/latest-extract", "/admin/image-selector", "/admin/barcode-scanner", "/admin/global-identifier-key", "/admin/cardview-weekly", "/admin/priority-products") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('dashboardAdmin')}>
                      <span>Dashboard Admin</span>
                      <span className="nav-toggle-icon">{expanded.dashboardAdmin ? '×' : '+'}</span>
                    </li>
                    {expanded.dashboardAdmin && (
                      <>
                        <NavSubLink to="/admin/latest-extract">Product Data Sync</NavSubLink>
                        <NavSubLink to="/admin/image-selector">Image Selector</NavSubLink>
                        <NavSubLink to="/admin/barcode-scanner">Barcode Scanner</NavSubLink>
                        <NavSubLink to="/admin/global-identifier-key">Global Identifier Key</NavSubLink>
                        <NavSubLink to="/admin/cardview-weekly">CardView Weekly</NavSubLink>
                        <NavSubLink to="/admin/priority-products">Priority Products</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/platform-status", "/admin/file-count", "/admin/content-protection", "/admin/login-page-designer") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('platformAdmin')}>
                      <span>Platform Admin</span>
                      <span className="nav-toggle-icon">{expanded.platformAdmin ? '×' : '+'}</span>
                    </li>
                    {expanded.platformAdmin && (
                      <>
                        <NavSubLink to="/admin/platform-status">Status</NavSubLink>
                        <NavSubLink to="/admin/file-count">File Count</NavSubLink>
                        <NavSubLink to="/admin/content-protection">Content Protection</NavSubLink>
                        <NavSubLink to="/admin/login-page-designer">Login Page Designer</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {Number(currentUser?.user_group) === 99 && showSection("/admin/ios-builder") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('appAdmin')}>
                      <span>App Admin</span>
                      <span className="nav-toggle-icon">{expanded.appAdmin ? '×' : '+'}</span>
                    </li>
                    {expanded.appAdmin && (
                      <NavSubLink to="/admin/ios-builder">iOS Builder</NavSubLink>
                    )}
                  </>
                )}

                {showSection("/admin/email-lists", "/admin/email-campaigns", "/admin/email-template", "/admin/email-config", "/admin/email-flows") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection("emailAdmin")}>
                      <span>Email Admin</span>
                      <span className="nav-toggle-icon">{expanded.emailAdmin ? "×" : "+"}</span>
                    </li>
                    {expanded.emailAdmin && (
                      <>
                        <NavSubLink to="/admin/email-lists">Email Lists</NavSubLink>
                        <NavSubLink to="/admin/email-campaigns">Email Campaigns</NavSubLink>
                        <NavSubLink to="/admin/email-template">Email Template</NavSubLink>
                        <NavSubLink to="/admin/email-config">Email Config</NavSubLink>
                        <NavSubLink to="/admin/email-flows">Email Flows</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/stripe-config", "/admin/subscriptions", "/admin/package-permissions", "/admin/stripe-events", "/admin/stripe-analytics") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection("billing")}>
                      <span>Billing Admin</span>
                      <span className="nav-toggle-icon">{expanded.billing ? "×" : "+"}</span>
                    </li>
                    {expanded.billing && (
                      <>
                        <NavSubLink to="/admin/stripe-config">Stripe Config</NavSubLink>
                        <NavSubLink to="/admin/subscriptions">Subscriptions</NavSubLink>
                        <NavSubLink to="/admin/package-permissions">Package Permissions</NavSubLink>
                        <NavSubLink to="/admin/stripe-events">Stripe Events</NavSubLink>
                        <NavSubLink to="/admin/stripe-analytics">Stripe Analytics</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/support-tickets", "/admin/support-manager") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('supportAdmin')}>
                      <span>Support Admin</span>
                      <span className="nav-toggle-icon">{expanded.supportAdmin ? '×' : '+'}</span>
                    </li>
                    {expanded.supportAdmin && (
                      <>
                        <NavSubLink to="/admin/support-tickets">Support Tickets</NavSubLink>
                        <NavSubLink to="/admin/support-manager">Support Manager</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/users", "/admin/user-audit-log", "/admin/product-analytics") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('userAdmin')}>
                      <span>User Admin</span>
                      <span className="nav-toggle-icon">{expanded.userAdmin ? '×' : '+'}</span>
                    </li>
                    {expanded.userAdmin && (
                      <>
                        <NavSubLink to="/admin/users">User Management</NavSubLink>
                        <NavSubLink to="/admin/user-audit-log">User Audit Log</NavSubLink>
                        <NavSubLink to="/admin/product-analytics">Product Analytics</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/new-products", "/admin/discontinued-products", "/admin/price-changes", "/admin/custom-reports", "/admin/export-library", "/admin/zuno", "/admin/zuno-saved", "/admin/zuno-nutrition") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('enterprise')}>
                      <span>Enterprise</span>
                      <span className="nav-toggle-icon">{expanded.enterprise ? '×' : '+'}</span>
                    </li>
                    {expanded.enterprise && (
                      <>
                        <NavSubLink to="/admin/new-products">New Products</NavSubLink>
                        <NavSubLink to="/admin/discontinued-products">Discontinued Products</NavSubLink>
                        <NavSubLink to="/admin/price-changes">Price Changes</NavSubLink>
                        <NavSubLink to="/admin/custom-reports">Custom Reports</NavSubLink>
                        <NavSubLink to="/admin/export-library">Export Library</NavSubLink>
                        <NavSubLink to="/admin/zuno">Zuno</NavSubLink>
                        <NavSubLink to="/admin/zuno-saved">Zuno - Saved Analysis</NavSubLink>
                        <NavSubLink to="/admin/zuno-nutrition">Zuno - Nutrition</NavSubLink>
                      </>
                    )}
                  </>
                )}

                {showSection("/admin/news-articles", "/admin/enterprise-news-articles", "/admin/add-news-article", "/admin/news-article-admin", "/admin/news-article-analytics") && (
                  <>
                    <li className="nav-section-heading" onClick={() => toggleSection('news')}>
                      <span>News</span>
                      <span className="nav-toggle-icon">{expanded.news ? '×' : '+'}</span>
                    </li>
                    {expanded.news && (
                      <>
                        <NavSubLink to="/admin/news-articles">News Articles</NavSubLink>
                        <NavSubLink to="/admin/enterprise-news-articles">Enterprise News Articles</NavSubLink>
                        <NavSubLink to="/admin/add-news-article">Add News Article</NavSubLink>
                        <NavSubLink to="/admin/news-article-admin">News Article Admin</NavSubLink>
                        <NavSubLink to="/admin/news-article-analytics">News Analytics</NavSubLink>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            <li className="nav-section-heading" onClick={() => toggleSection('account')}>
              <span>Account</span>
              <span className="nav-toggle-icon">{expanded.account ? '×' : '+'}</span>
            </li>
            {expanded.account && (
              <>
                <NavSubLink to="/profile">Profile</NavSubLink>
                <NavSubLink to="/premium">Premium</NavSubLink>
                <NavSubLink to="/subscription">Subscription Settings</NavSubLink>
                <NavSubLink to="/contact">Contact Us</NavSubLink>
                <NavSubLink to="/support-tickets">My Tickets</NavSubLink>
              </>
            )}

            <li>
              <button onClick={handleLogout}>Logout</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
}

export default NavigationBar;
